import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Upload, Search, TrendingUp, TrendingDown, Wallet, Link2, X,
  AlertCircle, CheckCircle2, FileText, Trash2, Building2, Tag, Eye,
  ArrowDownRight, ArrowUpRight, Receipt, FileSpreadsheet,
} from 'lucide-react';
import api from '../lib/api';

const fmtEUR = (n) => (Number(n) || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' EUR';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';

const STATUS_BADGE = {
  unmatched: { label: 'Da riconciliare', color: 'bg-amber-50 text-amber-700 border-amber-300' },
  reconciled: { label: 'Riconciliata', color: 'bg-green-50 text-green-700 border-green-300' },
  ignored: { label: 'Ignorata', color: 'bg-slate-100 text-slate-500 border-slate-300' },
};

export default function BankStatementsPage() {
  const [transactions, setTransactions] = useState([]);
  const [statements, setStatements] = useState([]);
  const [stats, setStats] = useState(null);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtri
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('__all__');
  const [filterType, setFilterType] = useState('__all__');
  const [filterSite, setFilterSite] = useState('__all__');
  const [filterAccount, setFilterAccount] = useState('');
  const [filterCategory, setFilterCategory] = useState('__all__');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  // Upload
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  // Edit dialog
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState(null);

  // Matches dialog
  const [showMatches, setShowMatches] = useState(false);
  const [matches, setMatches] = useState({ invoices: [], ses_matrices: [], expense_claims: [] });
  const [currentTxn, setCurrentTxn] = useState(null);

  const load = async () => {
    try {
      const params = { limit: 500 };
      if (filterStatus !== '__all__') params.status = filterStatus;
      if (filterType !== '__all__') params.type = filterType;
      if (filterSite !== '__all__') params.site_id = filterSite;
      if (filterAccount) params.account = filterAccount;
      if (filterCategory !== '__all__') params.bank_category = filterCategory;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      if (search) params.search = search;

      const [tRes, sRes, statRes, sitesRes] = await Promise.all([
        api.get('/bank-statements/transactions', { params }),
        api.get('/bank-statements/statements').catch(() => ({ data: [] })),
        api.get('/bank-statements/stats', { params: { ...(filterDateFrom ? { date_from: filterDateFrom } : {}), ...(filterDateTo ? { date_to: filterDateTo } : {}) } }).catch(() => ({ data: null })),
        api.get('/sites').catch(() => ({ data: [] })),
      ]);
      setTransactions(tRes.data || []);
      setStatements(sRes.data || []);
      setStats(statRes.data);
      setSites(sitesRes.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filterStatus, filterType, filterSite, filterAccount, filterCategory, filterDateFrom, filterDateTo]);
  useEffect(() => { const t = setTimeout(load, 400); return () => clearTimeout(t); }, [search]); // eslint-disable-line

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await api.post('/bank-statements/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadResult(res.data);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore upload');
    } finally { setUploading(false); }
  };

  const openEdit = (txn) => {
    setEditForm({
      id: txn.id,
      site_id: txn.site_id || '__none__',
      custom_category: txn.custom_category || '',
      notes: txn.notes || '',
      status: txn.status || 'unmatched',
      linked_invoice_number: txn.linked_invoice_number || '',
      _txn: txn,
    });
    setShowEdit(true);
  };

  const handleSaveEdit = async () => {
    try {
      const payload = {
        site_id: editForm.site_id === '__none__' ? null : editForm.site_id,
        custom_category: editForm.custom_category,
        notes: editForm.notes,
        status: editForm.status,
        linked_invoice_number: editForm.linked_invoice_number,
      };
      await api.put(`/bank-statements/transactions/${editForm.id}`, payload);
      setShowEdit(false); load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleProposeMatches = async (txn) => {
    setCurrentTxn(txn);
    try {
      const res = await api.get(`/bank-statements/transactions/${txn.id}/proposed-matches`);
      setMatches(res.data);
      setShowMatches(true);
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleLinkInvoice = async (inv) => {
    try {
      await api.put(`/bank-statements/transactions/${currentTxn.id}`, {
        linked_invoice_id: inv.id,
        linked_invoice_number: inv.number,
      });
      setShowMatches(false); load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };
  const handleLinkSES = async (s) => {
    try {
      await api.put(`/bank-statements/transactions/${currentTxn.id}`, { linked_ses_id: s.id });
      setShowMatches(false); load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };
  const handleLinkClaim = async (c) => {
    try {
      await api.put(`/bank-statements/transactions/${currentTxn.id}`, { linked_expense_claim_id: c.id });
      setShowMatches(false); load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleDeleteStatement = async (stmt) => {
    if (!window.confirm(`Eliminare il file ${stmt.original_name} e tutte le ${stmt.txn_count_new} transazioni associate?`)) return;
    try { await api.delete(`/bank-statements/statements/${stmt.id}`); load(); }
    catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleResetFilters = () => {
    setSearch(''); setFilterStatus('__all__'); setFilterType('__all__'); setFilterSite('__all__');
    setFilterAccount(''); setFilterCategory('__all__'); setFilterDateFrom(''); setFilterDateTo('');
  };

  // Estrae lista categorie distinte
  const bankCategories = [...new Set(transactions.map(t => t.bank_category).filter(Boolean))].sort();

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-slate-400">Caricamento...</div></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="bank-statements-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Contabilita' / Estratti Conto</h1>
        <label className="cursor-pointer">
          <input type="file" className="hidden" accept=".xlsx,.xls,.pdf" onChange={e => e.target.files?.[0] && handleUpload(e.target.files[0])} disabled={uploading} data-testid="upload-statement-input" />
          <span className={`inline-flex items-center px-4 py-2 rounded-md border ${uploading ? 'opacity-60' : ''} bg-indigo-600 text-white hover:bg-indigo-700 cursor-pointer`}>
            <Upload size={16} className="mr-2" /> {uploading ? 'Caricamento...' : 'Carica Estratto Conto'}
          </span>
        </label>
      </div>

      {/* STATS */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card className="border-green-200 bg-green-50/40">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-green-700 font-medium">Entrate</p>
                  <p className="text-xl font-bold text-green-900 mt-1">{fmtEUR(stats.total_income)}</p>
                </div>
                <ArrowUpRight size={24} className="text-green-600" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50/40">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-red-700 font-medium">Uscite</p>
                  <p className="text-xl font-bold text-red-900 mt-1">{fmtEUR(Math.abs(stats.total_expense))}</p>
                </div>
                <ArrowDownRight size={24} className="text-red-600" />
              </div>
            </CardContent>
          </Card>
          <Card className={`border ${stats.net >= 0 ? 'border-emerald-200 bg-emerald-50/40' : 'border-orange-200 bg-orange-50/40'}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-xs font-medium ${stats.net >= 0 ? 'text-emerald-700' : 'text-orange-700'}`}>Saldo Netto</p>
                  <p className={`text-xl font-bold mt-1 ${stats.net >= 0 ? 'text-emerald-900' : 'text-orange-900'}`}>{fmtEUR(stats.net)}</p>
                </div>
                <Wallet size={24} className={stats.net >= 0 ? 'text-emerald-600' : 'text-orange-600'} />
              </div>
            </CardContent>
          </Card>
          <Card className="border-amber-200 bg-amber-50/40">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-amber-700 font-medium">Da Riconciliare</p>
                  <p className="text-xl font-bold text-amber-900 mt-1">{stats.by_status?.unmatched || 0}<span className="text-sm font-normal text-amber-700"> / {stats.transaction_count}</span></p>
                </div>
                <AlertCircle size={24} className="text-amber-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="transactions">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="transactions" data-testid="tab-transactions">Transazioni ({transactions.length})</TabsTrigger>
          <TabsTrigger value="breakdown" data-testid="tab-breakdown">Analisi</TabsTrigger>
          <TabsTrigger value="files" data-testid="tab-files">File Caricati ({statements.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="transactions" className="space-y-4 mt-4">
          {/* FILTERS */}
          <div className="flex flex-col lg:flex-row gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <Input className="pl-9" placeholder="Cerca operazione, beneficiario..." value={search} onChange={e => setSearch(e.target.value)} data-testid="txn-search" />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="lg:w-36"><SelectValue placeholder="Tipo" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutte</SelectItem>
                <SelectItem value="income">Entrate</SelectItem>
                <SelectItem value="expense">Uscite</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="lg:w-44"><SelectValue placeholder="Stato" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutti gli stati</SelectItem>
                <SelectItem value="unmatched">Da riconciliare</SelectItem>
                <SelectItem value="reconciled">Riconciliate</SelectItem>
                <SelectItem value="ignored">Ignorate</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterSite} onValueChange={setFilterSite}>
              <SelectTrigger className="lg:w-44"><SelectValue placeholder="Cantiere" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutti i cantieri</SelectItem>
                {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="lg:w-52"><SelectValue placeholder="Categoria banca" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutte categorie</SelectItem>
                {bankCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input type="date" className="lg:w-40" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} placeholder="Da" />
            <Input type="date" className="lg:w-40" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} placeholder="A" />
            <Button variant="ghost" size="sm" onClick={handleResetFilters} className="text-xs"><X size={12} className="mr-1" /> Reset</Button>
          </div>

          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead className="min-w-[260px]">Operazione</TableHead>
                    <TableHead className="hidden md:table-cell">Categoria</TableHead>
                    <TableHead className="hidden lg:table-cell">Cantiere</TableHead>
                    <TableHead className="text-right">Importo</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead className="w-32">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Nessuna transazione. Carica un estratto conto.</TableCell></TableRow>
                  ) : transactions.map(t => {
                    const cfg = STATUS_BADGE[t.status] || STATUS_BADGE.unmatched;
                    const isIncome = t.amount > 0;
                    const linked = t.linked_invoice_number || t.linked_invoice_id || t.linked_ses_id || t.linked_expense_claim_id;
                    return (
                      <TableRow key={t.id} className="hover:bg-slate-50" data-testid={`txn-row-${t.id}`}>
                        <TableCell className="text-sm">{fmtDate(t.date)}</TableCell>
                        <TableCell>
                          <div className="text-sm font-medium text-slate-900">{t.operation}</div>
                          {t.details && <div className="text-xs text-slate-500 mt-0.5 line-clamp-2 max-w-md">{t.details}</div>}
                          {linked && <Badge variant="outline" className="mt-1 bg-blue-50 text-blue-700 border-blue-200 text-[10px]"><Link2 size={9} className="mr-0.5" /> {t.linked_invoice_number || 'Collegato'}</Badge>}
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <Badge variant="outline" className="text-xs">{t.bank_category || '-'}</Badge>
                          {t.custom_category && <Badge variant="outline" className="ml-1 bg-purple-50 text-purple-700 border-purple-200 text-xs"><Tag size={9} className="mr-0.5" /> {t.custom_category}</Badge>}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-sm">
                          {t.site_name ? <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200"><Building2 size={9} className="mr-0.5" /> {t.site_name}</Badge> : <span className="text-xs text-slate-400">-</span>}
                        </TableCell>
                        <TableCell className={`text-right text-sm font-bold ${isIncome ? 'text-green-700' : 'text-red-700'}`}>
                          {isIncome ? '+' : ''}{fmtEUR(t.amount)}
                        </TableCell>
                        <TableCell><Badge variant="outline" className={cfg.color}>{cfg.label}</Badge></TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" className="p-1 h-7 w-7" onClick={() => openEdit(t)} title="Modifica/Tag cantiere"><Tag size={14} /></Button>
                            <Button variant="ghost" size="sm" className="p-1 h-7 w-7" onClick={() => handleProposeMatches(t)} title="Proponi match fattura/SES"><Link2 size={14} className="text-indigo-600" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="breakdown" className="mt-4 space-y-4">
          {stats && (
            <>
              <Card className="border-slate-200">
                <CardHeader className="pb-2"><CardTitle className="text-sm">Per Categoria Bancaria</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-1.5 max-h-96 overflow-y-auto">
                    {Object.entries(stats.by_bank_category || {}).sort((a, b) => Math.abs(b[1]) - Math.abs(a[1])).map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between text-sm border-b border-slate-100 py-1.5">
                        <span className="text-slate-700">{k}</span>
                        <span className={`font-semibold ${v >= 0 ? 'text-green-700' : 'text-red-700'}`}>{v >= 0 ? '+' : ''}{fmtEUR(v)}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="border-slate-200">
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Per Cantiere</CardTitle></CardHeader>
                  <CardContent className="space-y-1.5">
                    {Object.entries(stats.by_site || {}).sort((a, b) => Math.abs(b[1]) - Math.abs(a[1])).map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between text-sm border-b border-slate-100 py-1.5">
                        <span className="text-slate-700">{k}</span>
                        <span className={`font-semibold ${v >= 0 ? 'text-green-700' : 'text-red-700'}`}>{v >= 0 ? '+' : ''}{fmtEUR(v)}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
                <Card className="border-slate-200">
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Per Conto/Carta</CardTitle></CardHeader>
                  <CardContent className="space-y-1.5">
                    {Object.entries(stats.by_account || {}).map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between text-sm border-b border-slate-100 py-1.5">
                        <span className="text-slate-700 truncate">{k}</span>
                        <span className={`font-semibold ${v >= 0 ? 'text-green-700' : 'text-red-700'}`}>{v >= 0 ? '+' : ''}{fmtEUR(v)}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="files" className="mt-4">
          <Card className="border-slate-200">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data Upload</TableHead>
                    <TableHead>File</TableHead>
                    <TableHead>Formato</TableHead>
                    <TableHead className="text-right">Tx Totali</TableHead>
                    <TableHead className="text-right">Tx Nuove</TableHead>
                    <TableHead className="text-right">Duplicate</TableHead>
                    <TableHead className="w-24">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {statements.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Nessun file caricato</TableCell></TableRow>
                  ) : statements.map(s => (
                    <TableRow key={s.id}>
                      <TableCell className="text-sm">{fmtDate(s.uploaded_at)}</TableCell>
                      <TableCell className="text-sm font-medium">{s.original_name}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs uppercase">{s.format}</Badge></TableCell>
                      <TableCell className="text-right text-sm">{s.txn_count_total}</TableCell>
                      <TableCell className="text-right text-sm text-green-700 font-semibold">{s.txn_count_new}</TableCell>
                      <TableCell className="text-right text-sm text-amber-700">{s.txn_count_duplicate}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <a href={`${process.env.REACT_APP_BACKEND_URL}${s.file_url}`} target="_blank" rel="noreferrer">
                            <Button variant="ghost" size="sm" className="p-1 h-7 w-7"><FileText size={14} /></Button>
                          </a>
                          <Button variant="ghost" size="sm" className="p-1 h-7 w-7" onClick={() => handleDeleteStatement(s)}><Trash2 size={14} className="text-red-600" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* UPLOAD RESULT */}
      <Dialog open={!!uploadResult} onOpenChange={(o) => !o && setUploadResult(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CheckCircle2 className="text-green-600" /> Upload Completato</DialogTitle></DialogHeader>
          {uploadResult && (
            <div className="space-y-2 text-sm">
              <p><strong>File:</strong> {uploadResult.filename}</p>
              <p>Totale righe trovate: <strong>{uploadResult.total_parsed}</strong></p>
              <p className="text-green-700">Nuove transazioni: <strong>{uploadResult.new_transactions}</strong></p>
              <p className="text-amber-700">Duplicate (saltate): <strong>{uploadResult.duplicates_skipped}</strong></p>
              <p className="text-xs text-slate-500 mt-3">Le duplicate sono state ignorate in base a hash (data + importo + descrizione). Procedi a taggare le transazioni con cantiere e categoria custom.</p>
            </div>
          )}
          <DialogFooter><Button onClick={() => setUploadResult(null)}>OK</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EDIT TXN */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Modifica Transazione</DialogTitle><DialogDescription>Assegna cantiere, categoria custom, note.</DialogDescription></DialogHeader>
          {editForm && (
            <div className="space-y-3">
              <Card className="bg-slate-50 border-slate-200">
                <CardContent className="p-3 text-sm">
                  <p><strong>{fmtDate(editForm._txn.date)}</strong> - {editForm._txn.operation}</p>
                  <p className={`text-lg font-bold mt-1 ${editForm._txn.amount > 0 ? 'text-green-700' : 'text-red-700'}`}>{editForm._txn.amount > 0 ? '+' : ''}{fmtEUR(editForm._txn.amount)}</p>
                  <p className="text-xs text-slate-500 mt-1">Cat. banca: {editForm._txn.bank_category || '-'}</p>
                </CardContent>
              </Card>
              <div className="space-y-1.5">
                <Label>Cantiere</Label>
                <Select value={editForm.site_id} onValueChange={v => setEditForm(f => ({ ...f, site_id: v }))}>
                  <SelectTrigger data-testid="edit-site"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Nessuno</SelectItem>
                    {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Categoria personalizzata</Label>
                <Input value={editForm.custom_category} onChange={e => setEditForm(f => ({ ...f, custom_category: e.target.value }))} placeholder="Es. Costo cantiere, Stipendio, Carburante..." data-testid="edit-custom-cat" />
              </div>
              <div className="space-y-1.5">
                <Label>N. Fattura collegata</Label>
                <Input value={editForm.linked_invoice_number} onChange={e => setEditForm(f => ({ ...f, linked_invoice_number: e.target.value }))} placeholder="Es. 2026/123" />
              </div>
              <div className="space-y-1.5">
                <Label>Stato</Label>
                <Select value={editForm.status} onValueChange={v => setEditForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unmatched">Da riconciliare</SelectItem>
                    <SelectItem value="reconciled">Riconciliata</SelectItem>
                    <SelectItem value="ignored">Ignorata</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Note</Label>
                <Textarea value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} rows={2} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(false)}>Annulla</Button>
            <Button onClick={handleSaveEdit} data-testid="edit-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MATCHES DIALOG */}
      <Dialog open={showMatches} onOpenChange={setShowMatches}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Proposte di Riconciliazione</DialogTitle>
            <DialogDescription>
              {currentTxn && <>Match per importo simile a <strong className={currentTxn.amount > 0 ? 'text-green-700' : 'text-red-700'}>{fmtEUR(currentTxn.amount)}</strong> ({currentTxn.operation})</>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <p className="text-sm font-semibold mb-1 flex items-center gap-1"><Receipt size={14} /> Fatture ({matches.invoices.length})</p>
              {matches.invoices.length === 0 ? <p className="text-xs text-slate-400">Nessuna fattura con importo compatibile</p> : matches.invoices.map(i => (
                <div key={i.id} className="flex items-center justify-between p-2 border rounded text-sm hover:bg-blue-50">
                  <div>
                    <p className="font-medium">{i.number} - {i.client}</p>
                    <p className="text-xs text-slate-500">{fmtDate(i.date)} - {fmtEUR(i.amount)} (Δ {fmtEUR(i.delta)})</p>
                  </div>
                  <Button size="sm" onClick={() => handleLinkInvoice(i)}><Link2 size={12} className="mr-1" /> Collega</Button>
                </div>
              ))}
            </div>
            <div>
              <p className="text-sm font-semibold mb-1 flex items-center gap-1"><FileSpreadsheet size={14} /> Matrici SES ({matches.ses_matrices.length})</p>
              {matches.ses_matrices.length === 0 ? <p className="text-xs text-slate-400">Nessuna matrice SES con importo compatibile</p> : matches.ses_matrices.map(s => (
                <div key={s.id} className="flex items-center justify-between p-2 border rounded text-sm hover:bg-indigo-50">
                  <div>
                    <p className="font-medium">WO {s.work_order} {s.ses_code ? `(SES ${s.ses_code})` : ''}</p>
                    <p className="text-xs text-slate-500">{fmtDate(s.date)} - {fmtEUR(s.amount)} (Δ {fmtEUR(s.delta)}) - {s.description}</p>
                  </div>
                  <Button size="sm" onClick={() => handleLinkSES(s)}><Link2 size={12} className="mr-1" /> Collega</Button>
                </div>
              ))}
            </div>
            <div>
              <p className="text-sm font-semibold mb-1 flex items-center gap-1"><Wallet size={14} /> Rimborsi Spese ({matches.expense_claims.length})</p>
              {matches.expense_claims.length === 0 ? <p className="text-xs text-slate-400">Nessun rimborso con importo compatibile</p> : matches.expense_claims.map(c => (
                <div key={c.id} className="flex items-center justify-between p-2 border rounded text-sm hover:bg-rose-50">
                  <div>
                    <p className="font-medium">{c.description}</p>
                    <p className="text-xs text-slate-500">{fmtDate(c.date)} - {fmtEUR(c.amount)} (Δ {fmtEUR(c.delta)})</p>
                  </div>
                  <Button size="sm" onClick={() => handleLinkClaim(c)}><Link2 size={12} className="mr-1" /> Collega</Button>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowMatches(false)}>Chiudi</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
